import { Tooltip } from '@/components/ui/v3/Tooltip';
import type {
  DirectoryHandle,
  DirectoryProviderUnit,
} from '@/hooks/useProviderDirectory';
import { cn } from '@/lib/utils';
import {
  formatCadenceTooltip,
  formatDirectoryHandleValue,
  getHandleTypeIcon,
  getHandleTypeIconColor,
  getHandleTypeLabel,
} from '@/utils/providerDirectory';

interface DirectoryContactCardProps {
  handle: DirectoryHandle;
  unit?: DirectoryProviderUnit;
  isSelected?: boolean;
  compact?: boolean;
  showActions?: boolean;
  onSelect?: (handle: DirectoryHandle) => void;
  onSetPrimary?: (handle: DirectoryHandle) => void;
  isUpdatingPrimary?: boolean;
  className?: string;
}

export const DirectoryContactCard = ({
  handle,
  unit,
  isSelected = false,
  compact = false,
  showActions = false,
  onSelect,
  onSetPrimary,
  isUpdatingPrimary = false,
  className,
}: DirectoryContactCardProps) => {
  const Icon = getHandleTypeIcon(handle.handle?.handle_type);
  const iconColorClass = getHandleTypeIconColor(handle.handle?.handle_type);
  const handleValue = formatDirectoryHandleValue(handle);
  const cadenceTooltip = formatCadenceTooltip(handle);
  const handleTypeLabel = getHandleTypeLabel(handle.handle?.handle_type);

  const CardWrapper = onSelect ? 'button' : 'div';

  return (
    <CardWrapper
      type={onSelect ? 'button' : undefined}
      onClick={onSelect ? () => onSelect(handle) : undefined}
      className={cn(
        'w-full text-left border rounded-md px-4 transition-colors',
        compact ? 'py-2' : 'py-3',
        isSelected
          ? 'border-blue-500 bg-blue-50'
          : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/40',
        onSelect && 'cursor-pointer',
        className,
      )}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 mt-0.5">
          <Icon className={cn('h-5 w-5', iconColorClass)} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header row */}
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={cn(
                'font-medium text-gray-900',
                compact ? 'text-sm' : 'text-base',
              )}
            >
              {handle.handle?.name || 'Untitled contact'}
            </span>
            {handle.is_preferred && (
              <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                Preferred
              </span>
            )}
            {handle.capabilities &&
              handle.capabilities !== 'RECORDS_AND_BILLS' && (
                <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700">
                  {handle.capabilities.replace(/_/g, ' ').toLowerCase()}
                </span>
              )}
          </div>

          {/* Value row */}
          <div
            className={cn(
              'text-gray-700 truncate',
              compact ? 'text-xs' : 'text-sm',
            )}
          >
            {handleValue}
            {!compact && handleTypeLabel && (
              <span className="text-xs text-gray-500 ml-2">
                • {handleTypeLabel}
              </span>
            )}
          </div>

          {/* Metadata row */}
          {(handle.voice_ai_cadence_days || (unit && !compact)) && (
            <div
              className={cn(
                'flex flex-wrap items-center gap-3 text-gray-500',
                compact ? 'text-xs mt-0.5' : 'text-xs mt-1',
              )}
            >
              {handle.voice_ai_cadence_days && (
                <Tooltip content={cadenceTooltip || ''}>
                  <span className="cursor-help">
                    {handle.voice_ai_cadence_days}d cadence
                  </span>
                </Tooltip>
              )}
              {unit && !compact && (
                <span className="text-gray-400">{unit.name}</span>
              )}
            </div>
          )}

          {/* Notes */}
          {!compact && handle.notes && (
            <div className="text-xs text-gray-600 mt-1 italic">
              {handle.notes}
            </div>
          )}
        </div>

        {/* Actions */}
        {showActions && !handle.is_preferred && (
          <div className="flex-shrink-0">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onSetPrimary?.(handle);
              }}
              disabled={isUpdatingPrimary}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
            >
              {isUpdatingPrimary ? 'Setting...' : 'Set as primary'}
            </button>
          </div>
        )}
      </div>
    </CardWrapper>
  );
};
